/*
Arboretum screen — list of species the user has captured, with Paris-wide
stats and discovery date. Mirrors the Android ArboretumScreen, minus the
seasonal selector and the per-species photo gallery (both pending until the
capture flow + photos store are wired up).

Data sources:
- Captures: IndexedDB, grouped by speciesIndex (CaptureRepository).
- Species names: species-index.json reverse-lookup (latin binomial).
- Friendly French names + Paris-wide count: species-info.json.
- Total dataset species count: dataset-stats.json (header denominator).
*/

import { navigate, kPaths } from '../router'
import { groupCapturesBySpecies, type SpeciesCaptureGroup } from '../data/captureRepository'
import { ensureSpeciesIndex, speciesNameFor, type SpeciesName } from '../data/speciesIndex'
import { ensureSpeciesInfo, speciesInfoFor } from '../data/speciesInfo'
import { ensureDatasetStats } from '../data/datasetStats'
import { loadPhoto } from '../data/photoStore'
import { getSeason } from '../data/seasonStore'
import { renderSeasonSelector, wireSeasonSelector, renderArchiveBanner } from './components/SeasonSelector'
import { openLightbox, type LightboxPhoto } from './components/PhotoLightbox'
import { renderEmptyState } from './components/EmptyState'
import { renderSkeletonBlock, renderSkeletonListCard } from './components/Skeleton'
import { openComparePicker } from './components/ComparePicker'

const kFrenchDate = new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })  // FR locale formatter for first-capture timestamps
const kFrenchNumber = new Intl.NumberFormat('fr-FR')  // thousands separator for Paris counts

interface ViewModel
{
	totalSpecies: number
	groups: SpeciesCaptureGroup[]
	loadError: string | null
}

const kMaxThumbsPerCard: number = 3  // up to N thumbnails strip per Arboretum card

let fActiveObjectUrls: string[] = []  // photo blob URLs created during the current mount, revoked on remount
let fActiveUnsubscribe: (() => void) | null = null  // SeasonStore subscription, released on remount

/*
Mount the Arboretum screen. Renders an empty shell synchronously, then
hydrates async with capture + dataset data. Re-mounts cleanly on each
navigation (router clears innerHTML before calling this).
*/
export function mountArboretumScreen(inRoot: HTMLElement): void
{
	revokeObjectUrls()  // clean up any URLs from a prior mount
	if (fActiveUnsubscribe) { fActiveUnsubscribe(); fActiveUnsubscribe = null }

	inRoot.innerHTML = renderShell()
	wireBackNav(inRoot)
	// wireSeasonSelector emits the current season immediately, which triggers
	// the first hydrate via inOnChange — no need for a separate hydrate call.
	fActiveUnsubscribe = wireSeasonSelector(inRoot, () => { void hydrate(inRoot) })
}

function revokeObjectUrls(): void
{
	for (const vUrl of fActiveObjectUrls) URL.revokeObjectURL(vUrl)
	fActiveObjectUrls = []
}

/*
Render the static shell: header + a placeholder content area that hydrate()
will replace once the data is loaded. Keeps the screen visually stable
during the first render.
*/
function renderShell(): string
{
	return `
		<div class="screen arboretum">
			<header class="arboretum-header">
				<button class="nav-back" id="nav-back" aria-label="Retour">←</button>
				<h1 class="display arboretum-title">Arboretum</h1>
			</header>
			<div class="arboretum-toolbar">${renderSeasonSelector()}</div>
			<div class="arboretum-banner" id="arboretum-banner"></div>
			<div class="arboretum-subheader" id="arboretum-subheader">
				${renderSkeletonBlock({ width: '160px', height: '14px' })}
			</div>
			<div class="arboretum-list" id="arboretum-list" role="list">
				${renderSkeletonListCard()}
				${renderSkeletonListCard()}
				${renderSkeletonListCard()}
				${renderSkeletonListCard()}
			</div>
		</div>
	`
}

function wireBackNav(inRoot: HTMLElement): void
{
	inRoot.querySelector('#nav-back')?.addEventListener('click', () => navigate(kPaths.map()))
}

/*
Async hydration step: load captures + species metadata + dataset stats in
parallel, then swap the shell's subheader and list contents in one render
pass to avoid layout flicker.
*/
async function hydrate(inRoot: HTMLElement): Promise<void>
{
	revokeObjectUrls()  // dropping prior thumbnails — we're about to re-render
	const vSeason = getSeason()  // active season at hydrate time, drives the filter
	const vModel = await loadViewModel(vSeason)  // gather everything before painting once
	const vSubheader = inRoot.querySelector('#arboretum-subheader') as HTMLElement | null
	const vList = inRoot.querySelector('#arboretum-list') as HTMLElement | null
	const vBanner = inRoot.querySelector('#arboretum-banner') as HTMLElement | null
	if (!vSubheader || !vList) return
	if (vBanner) vBanner.innerHTML = renderArchiveBanner(vSeason) ?? ''

	if (vModel.loadError)
	{
		vSubheader.innerHTML = `<span class="arboretum-error">⚠ ${escapeHtml(vModel.loadError)}</span>`
		vList.innerHTML = ''
		return
	}

	const vDistinct = vModel.groups.length  // number of distinct species captured
	vSubheader.innerHTML = `
		<span class="arboretum-counter">
			<strong>${vDistinct}</strong> / ${vModel.totalSpecies} espèces
		</span>
	`

	if (vModel.groups.length === 0)
	{
		vList.innerHTML = renderEmptyState({
			variant: 'arboretum',
			title: "Pas encore d'arbre découvert",
			body: "Reviens ici après ta première capture sur la carte — tes espèces s'afficheront avec leurs stats parisiennes.",
			ctaLabel: '↗ Aller sur la carte',
			ctaHref: kPaths.map(),
		})
		return
	}

	vList.innerHTML = vModel.groups.map(renderCard).join('')
	vList.querySelectorAll<HTMLElement>('.arboretum-card').forEach((inCard) => {
		inCard.addEventListener('click', () => {
			const vIndex = Number(inCard.dataset.speciesIndex)  // speciesIndex stamped on the card via data-attr
			if (Number.isFinite(vIndex)) onCardClick(vIndex)
		})
		wireLongPress(inCard)
	})
	void hydrateThumbnails(vList, vModel.groups)
}

/*
Lazy-load the per-card thumbnail strips. We do this after the cards are
in the DOM so the list renders instantly, and the N×~200 KB photo Blobs
stream in opportunistically. createObjectURL handles cache busting; URLs
are tracked in module scope and revoked on the next mount.
*/
async function hydrateThumbnails(inList: HTMLElement, inGroups: SpeciesCaptureGroup[]): Promise<void>
{
	for (const vGroup of inGroups)
	{
		const vStrip = inList.querySelector<HTMLElement>(
			`[data-species-index="${vGroup.speciesIndex}"] .arboretum-thumbs`,
		)
		if (!vStrip) continue
		const vTake = vGroup.photoFilenames.slice(0, kMaxThumbsPerCard)  // most-recent first, capped
		const vPhotos: LightboxPhoto[] = []  // collected for this card's lightbox
		for (const vFile of vTake)
		{
			const vBlob = await loadPhoto(vFile)
			if (!vBlob) continue
			const vUrl = URL.createObjectURL(vBlob)
			fActiveObjectUrls.push(vUrl)
			const vImg = document.createElement('img')
			vImg.className = 'arboretum-thumb'
			vImg.alt = ''
			vImg.loading = 'lazy'
			vImg.src = vUrl
			const vIndex = vPhotos.length  // freeze the index for the click handler
			vImg.addEventListener('click', (inEv) => {
				inEv.stopPropagation()  // don't trigger the underlying card's click → species detail
				openLightbox(vPhotos, vIndex)
			})
			vStrip.appendChild(vImg)
			vPhotos.push({ url: vUrl })
		}
	}
}

/*
Bundle the three async loads into one ViewModel. We catch + record errors
rather than letting them bubble — the Arboretum should always render
something, even if the dataset assets are missing.
*/
async function loadViewModel(inSeason: number): Promise<ViewModel>
{
	try
	{
		const [, , vStats, vGroups] = await Promise.all([
			ensureSpeciesIndex(),
			ensureSpeciesInfo(),
			ensureDatasetStats(),
			groupCapturesBySpecies(inSeason),  // filter captures by selected season ordinal
		])
		return { totalSpecies: vStats.totalEspeces, groups: vGroups, loadError: null }
	}
	catch (vErr)
	{
		const vMsg = vErr instanceof Error ? vErr.message : 'Erreur inconnue'  // surface a readable message
		console.error('Arboretum hydration failed', vErr)
		return { totalSpecies: 0, groups: [], loadError: vMsg }
	}
}

/*
HTML for a single species card. Title falls back through Wikipedia-FR slug,
Latin binomial, then the raw speciesIndex. Click is wired by the parent.
*/
function renderCard(inGroup: SpeciesCaptureGroup): string
{
	const vName = speciesNameFor(inGroup.speciesIndex)  // {genre, espece, commonName} or null
	const vInfo = speciesInfoFor(inGroup.speciesIndex)  // SpeciesEntry or null

	const vCommon = vName?.commonName ?? null  // "Marronnier" if known
	const vLatin = formatLatin(vName)  // "Acer platanoides" or "Espèce inconnue"
	const vTitle = vCommon ?? vLatin  // primary heading: French common name when present
	const vSubtitle = vCommon ? vLatin : null  // latin under the common name when both exist
	const vParisCount = vInfo?.stats.count ?? null  // total in Paris, if known
	const vFirstDate = kFrenchDate.format(new Date(inGroup.firstCaptureAt))  // localized first-capture date
	const vCaptureLabel = inGroup.captureCount === 1 ? '1 capture' : `${inGroup.captureCount} captures`

	return `
		<button class="arboretum-card" role="listitem" data-species-index="${inGroup.speciesIndex}">
			<div class="arboretum-card-glyph" aria-hidden="true">🌳</div>
			<div class="arboretum-card-body">
				<div class="arboretum-card-title">${escapeHtml(vTitle)}</div>
				${vSubtitle ? `<div class="arboretum-card-subtitle"><em>${escapeHtml(vSubtitle)}</em></div>` : ''}
				<div class="arboretum-card-meta">
					${vParisCount !== null ? `<span>${kFrenchNumber.format(vParisCount)} à Paris</span>` : ''}
					<span>•</span>
					<span>${vCaptureLabel}</span>
				</div>
				<div class="arboretum-card-foot">Découvert le ${vFirstDate}</div>
				<div class="arboretum-thumbs" aria-hidden="true"></div>
			</div>
		</button>
	`
}


/*
Format a SpeciesName as a latin binomial. Both fields are capitalized at the
build script (genre title-case, espece lower-case in convention) — we
preserve them as-is. Falls back to the raw text "Espèce inconnue" when the
reverse index couldn't resolve the speciesIndex (data drift).
*/
function formatLatin(inName: SpeciesName | null): string
{
	if (!inName) return 'Espèce inconnue'
	const vEspece = inName.espece.length > 0 ? ` ${inName.espece}` : ''  // tolerate empty espece (rare in OpenData)
	return `${inName.genre}${vEspece}`
}

/*
TODO: navigate to a species detail screen when that route exists. For the
skeleton we just log — the click handler is in place so the wiring is
already there when Routes.species is added.
*/
function onCardClick(inSpeciesIndex: number): void
{
	navigate(kPaths.species(inSpeciesIndex))
}

/*
Minimal HTML escaping for user-supplied strings interpolated into innerHTML.
Species names come from the dataset (trusted) but we keep this honest in
case future fields surface free-text.
*/
/*
Long-press → "Comparer cette espèce avec…" picker.

iOS Safari constraint: contextmenu doesn't fire reliably from a long
touch in standalone PWA mode, so we hand-roll the gesture. We start
a 600 ms timer on touchstart / mousedown, cancel it on touchmove
beyond a small threshold or any touchend / mouseup before it
expires, and trigger the picker on timer fire. A subsequent click
event right after the trigger is suppressed via a `data-long-press`
flag so the user doesn't accidentally navigate to the species
detail too.

We also call preventDefault on contextmenu so right-click on
desktop maps to the same flow without showing the browser menu.
*/
const kLongPressMs: number = 600
const kLongPressMoveCancelPx: number = 8

function wireLongPress(inCard: HTMLElement): void
{
	let vTimer: number | null = null
	let vStartX: number = 0
	let vStartY: number = 0
	let vTriggered: boolean = false

	const vCancel = (): void => {
		if (vTimer !== null) { window.clearTimeout(vTimer); vTimer = null }
	}

	const vFire = (): void => {
		vTriggered = true
		vTimer = null
		const vIndex = Number(inCard.dataset.speciesIndex)
		if (!Number.isFinite(vIndex)) return
		const vTitle = inCard.querySelector<HTMLElement>('.arboretum-card-title')?.textContent?.trim() ?? 'cette espèce'
		// Optional haptic — confirms the long-press registered. Silent
		// no-op on iOS Safari, which is fine.
		try { navigator.vibrate?.(15) } catch { /* unsupported */ }
		void openComparePicker(vIndex, vTitle, {
			onPick: (inOther) => {
				if (inOther === vIndex) return  // safety: picker already filters the source out, but defend against stale taps
				navigateToCompare(vIndex, inOther)
			},
		})
	}

	inCard.addEventListener('touchstart', (inEv: TouchEvent) => {
		const vT = inEv.touches[0]
		if (!vT) return
		vTriggered = false
		vStartX = vT.clientX
		vStartY = vT.clientY
		vCancel()
		vTimer = window.setTimeout(vFire, kLongPressMs)
	}, { passive: true })

	inCard.addEventListener('touchmove', (inEv: TouchEvent) => {
		const vT = inEv.touches[0]
		if (!vT) return
		const vDx = Math.abs(vT.clientX - vStartX)
		const vDy = Math.abs(vT.clientY - vStartY)
		if (vDx > kLongPressMoveCancelPx || vDy > kLongPressMoveCancelPx) vCancel()
	}, { passive: true })

	inCard.addEventListener('touchend', () => { vCancel() })
	inCard.addEventListener('touchcancel', () => { vCancel() })

	// Desktop: mousedown / mouseup mirror the touch flow so
	// long-press works with a held primary click.
	inCard.addEventListener('mousedown', (inEv: MouseEvent) => {
		if (inEv.button !== 0) return  // left button only
		vTriggered = false
		vStartX = inEv.clientX
		vStartY = inEv.clientY
		vCancel()
		vTimer = window.setTimeout(vFire, kLongPressMs)
	})
	inCard.addEventListener('mousemove', (inEv: MouseEvent) => {
		if (vTimer === null) return
		const vDx = Math.abs(inEv.clientX - vStartX)
		const vDy = Math.abs(inEv.clientY - vStartY)
		if (vDx > kLongPressMoveCancelPx || vDy > kLongPressMoveCancelPx) vCancel()
	})
	inCard.addEventListener('mouseup', () => { vCancel() })
	inCard.addEventListener('mouseleave', () => { vCancel() })

	// Right-click / two-finger-tap → same trigger as long-press.
	inCard.addEventListener('contextmenu', (inEv: MouseEvent) => {
		inEv.preventDefault()
		vCancel()
		vFire()
	})

	// Suppress the synthetic click that follows a long-press on touch
	// devices (iOS Safari fires a click after touchend even if our
	// timer already triggered). We use the capture phase so we beat
	// the regular click handler.
	inCard.addEventListener('click', (inEv: MouseEvent) => {
		if (vTriggered)
		{
			vTriggered = false
			inEv.preventDefault()
			inEv.stopPropagation()
		}
	}, true)
}

function navigateToCompare(inSkA: number, inSkB: number): void
{
	navigate(kPaths.compare(inSkA, inSkB))
}

function escapeHtml(inText: string): string
{
	return inText
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;')
		.replace(/'/g, '&#39;')
}
